import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "@store/middleware";

import { getApplicationSpaBackendUrl } from "@utils";
import {
  type IBifrostAvailabilityQuery,
  type IBifrostAvailabilityResponse,
  type IBifrostBooking,
  type IBifrostBookingAvailabilityQuery,
  type IBifrostBookingCreateInput,
  type IBifrostBookingProductsQuery,
  type IBifrostBookingProductsResponse,
  type IBifrostBookingResponse,
  type IBifrostBookingsResponse,
  type IBifrostBookingUpdateInput,
  type IBifrostBookingWithPreparationResponse,
} from "@types";

export const BIFROST_BOOKINGS_API_REDUCER_KEY = "bifrostBookingsApi";

// A booking without dates would blow up date formatting downstream, so leave `from`/`to`
// empty instead of building `undefinedTundefinedZ` out of a partial response.
const withBookingDateTimes = <T extends IBifrostBooking>(booking: T): T => ({
  ...booking,
  from: booking.from_date == null || booking.from_time == null ? "" : `${booking.from_date}T${booking.from_time}Z`,
  to: booking.to_date == null || booking.to_time == null ? "" : `${booking.to_date}T${booking.to_time}Z`,
});

const withAvailabilitySlotDateTimes = (response: IBifrostAvailabilityResponse): IBifrostAvailabilityResponse => ({
  ...response,
  data: {
    ...response.data,
    items: response.data.items.map((item) => ({
      ...item,
      time_slots: item.time_slots.map((slot) => ({
        ...slot,
        from: `${item.date}T${slot.from_time}Z`,
        to: `${item.date}T${slot.to_time}Z`,
      })),
    })),
  },
});

export const bifrostBookingsApi = createApi({
  reducerPath: BIFROST_BOOKINGS_API_REDUCER_KEY,
  tagTypes: ["BIFROST_BOOKINGS", "BIFROST_AVAILABILITY"],
  baseQuery: (arguments_, api, extraOptions) =>
    baseQueryWithAuth({ baseUrl: getApplicationSpaBackendUrl() })(arguments_, api, {
      ...extraOptions,
      isExternalService: true,
      isUnauthorizedLogoutEnabled: false,
    }),
  endpoints: (builder) => ({
    getBifrostBookings: builder.query<IBifrostBookingsResponse, void>({
      query: () => ({
        url: "/bookings",
        method: "GET",
      }),
      transformResponse: (response: IBifrostBookingsResponse): IBifrostBookingsResponse => ({
        ...response,
        data: (response.data ?? []).map((booking) => withBookingDateTimes(booking)),
      }),
      providesTags: ["BIFROST_BOOKINGS"],
    }),
    getBifrostBooking: builder.query<IBifrostBookingWithPreparationResponse, IBifrostBooking["id"]>({
      query: (id) => ({
        url: `/bookings/${id}`,
        method: "GET",
      }),
      transformResponse: (
        response: IBifrostBookingWithPreparationResponse,
      ): IBifrostBookingWithPreparationResponse => ({
        ...response,
        data: withBookingDateTimes(response.data),
      }),
      providesTags: ["BIFROST_BOOKINGS"],
    }),
    getBifrostBookingProducts: builder.query<IBifrostBookingProductsResponse, IBifrostBookingProductsQuery>({
      query: (parameters) => ({
        url: "/booking-products",
        method: "GET",
        params: parameters,
      }),
    }),
    getBifrostAvailability: builder.query<IBifrostAvailabilityResponse, IBifrostAvailabilityQuery>({
      query: (parameters) => ({
        url: "/availability",
        method: "GET",
        params: parameters,
      }),
      transformResponse: withAvailabilitySlotDateTimes,
      providesTags: ["BIFROST_AVAILABILITY"],
    }),
    getBifrostBookingAvailability: builder.query<IBifrostAvailabilityResponse, IBifrostBookingAvailabilityQuery>({
      query: ({ id, ...parameters }) => ({
        url: `/bookings/${id}/availability`,
        method: "GET",
        params: parameters,
      }),
      transformResponse: withAvailabilitySlotDateTimes,
      providesTags: ["BIFROST_AVAILABILITY"],
    }),
    createBifrostBooking: builder.mutation<IBifrostBookingResponse, IBifrostBookingCreateInput>({
      query: (body) => ({
        url: "/bookings",
        method: "POST",
        body,
      }),
      transformResponse: (response: IBifrostBookingResponse): IBifrostBookingResponse => ({
        ...response,
        data: withBookingDateTimes(response.data),
      }),
      invalidatesTags: ["BIFROST_BOOKINGS", "BIFROST_AVAILABILITY"],
    }),
    updateBifrostBooking: builder.mutation<IBifrostBookingResponse, { id: number; body: IBifrostBookingUpdateInput }>({
      query: ({ id, body }) => ({
        url: `/bookings/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response: IBifrostBookingResponse): IBifrostBookingResponse => ({
        ...response,
        data: withBookingDateTimes(response.data),
      }),
      invalidatesTags: ["BIFROST_BOOKINGS", "BIFROST_AVAILABILITY"],
    }),
  }),
});

export const {
  useGetBifrostBookingsQuery,
  useGetBifrostBookingQuery,
  useGetBifrostBookingProductsQuery,
  useGetBifrostAvailabilityQuery,
  useGetBifrostBookingAvailabilityQuery,
  useCreateBifrostBookingMutation,
  useUpdateBifrostBookingMutation,
} = bifrostBookingsApi;
